'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import type Nativo from 'services/Nativo/entities';
import type { NativoCmd } from 'services/Nativo/entities';
import { useAdContext } from '@/app_lib/providers/AdProvider';
import { useUser } from '@/app_lib/providers/UserProvider';
import { noAdArticles } from '@/appconstants';

export const NativoData = () => {
  const { pageAdConfig: adConfig } = useAdContext();
  // biome-ignore lint/style/noNonNullAssertion: <This component is being called only when adConfig is defined>
  const pageAdConfig = adConfig!;
  const [isArctagReady, setIsArctagReady] = useState(false);
  const [shouldStartNativo, setShouldStartNativo] = useState(false);
  const [isNativoStarted, setIsNativoStarted] = useState(false);
  const { personalizedAds } = useUser();

  const hasNtvSearchQuery =
    typeof window !== 'undefined' &&
    (window.location.search.includes('prx_t') || window.location.search.includes('ntv_adpz'));

  const noNativoTags = [...noAdArticles, 'no-nativo'];

  const noNativoAds =
    noNativoTags.some((tag) => pageAdConfig.tags?.includes(tag)) || pageAdConfig.topics?.includes('no-nativo');

  useEffect(() => {
    // avoid race condition and intialize nativo cmd
    if (typeof window !== 'undefined') {
      window.ntv = window.ntv || ({} as NativoCmd);
      window.ntv.cmd = window.ntv.cmd || [];
    }

    // Set Nativo targeting
    const kvpMap: { [key: string]: string } = {};
    kvpMap.topics = pageAdConfig.topics ? pageAdConfig.topics.join(',') : '';
    kvpMap.arcspan = (window?.arcobj2?.activation?.['Google Ad Manager'] || []).join(',');
    kvpMap.ch = pageAdConfig.ch || ''; // Add channel
    kvpMap.tags = pageAdConfig.tags ? pageAdConfig.tags.join(',') : ''; // Add tags
    window.ntvConfig = window.ntvConfig || ({} as Nativo);
    window.ntvConfig.keyValues = kvpMap;

    if (window.ntvConfig?.keyValues && personalizedAds) {
      window.ntvConfig.keyValues.arcspan = (window?.arcobj2?.activation?.['Google Ad Manager'] || []).join(',');
    }

    // Listen for ArctagReady event to know when Arcspan was initialized
    const handleArctagReady = () => {
      setIsArctagReady(true);
    };

    window.addEventListener('arctagReady', handleArctagReady);

    // Attach callbacks to window object
    // The ArcSpan injection script in /Arcspan/index.tsx will call these handlers
    if (typeof window !== 'undefined') {
      window.onArcspanScriptLoad = () => {
        setShouldStartNativo(true);
      };

      window.onArcspanScriptError = () => {
        setShouldStartNativo(true);
      };
    }
    return () => {
      // Clean up the global functions
      if (typeof window !== 'undefined') {
        window.removeEventListener('arctagReady', handleArctagReady);
        delete window.onArcspanScriptLoad;
        delete window.onArcspanScriptError;
      }
    };
  }, [pageAdConfig.topics, personalizedAds]);

  useEffect(() => {
    if (noNativoAds) {
      return;
    }

    if (isArctagReady && personalizedAds) {
      let arcspanValue = '';

      if (window?.arcobj2?.activation) {
        arcspanValue = window.arcobj2.activation['Google Ad Manager']?.join(',') || '';
      }
      window.ntvConfig = window.ntvConfig || { keyValues: {} };
      window.ntvConfig.keyValues = {
        ...window.ntvConfig.keyValues,
        arcspan: arcspanValue,
      };
    }

    // Initialize Nativo when Arcspan script is ready or loading Arcspan script failed
    // Don't initialize if we're adding Nativo via search query params (since that is already adding the script and calling Start())
    if ((isArctagReady || shouldStartNativo) && !hasNtvSearchQuery) {
      setIsNativoStarted(true);
    }
  }, [personalizedAds, noNativoAds, isArctagReady, shouldStartNativo, hasNtvSearchQuery]);

  useEffect(() => {
    if (noNativoAds || isNativoStarted || personalizedAds === true || personalizedAds === null) {
      return;
    }
    const timer = setTimeout(() => {
      if (!isNativoStarted) {
        setIsNativoStarted(true);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [noNativoAds, isNativoStarted, personalizedAds]);

  useEffect(() => {
    if (!isNativoStarted) {
      return;
    }

    window.ntv.cmd.push(() => {
      window.PostRelease?.Start();
    });
  }, [isNativoStarted]);

  return (
    <>
      {!noNativoAds && (
        <Script
          data-ntv-set-no-auto-start
          id='nativo-js'
          src='https://s.ntv.io/serve/load.js'
          strategy='afterInteractive'
          {...(hasNtvSearchQuery && { 'data-ntv-pub-injection': true })}
        />
      )}

      {hasNtvSearchQuery && (
        <Script
          id='nativo-iframe'
          onLoad={() => {
            if (window.ntv?.cmd) {
              window.ntv.cmd.push(() => {
                window.PostRelease?.Start();
              });
            }
          }}
          src='https://assets.promotedarticle.com/iframeInjection.js'
        />
      )}
    </>
  );
};
